import React, { useState } from 'react';
import { QrCodeIcon, DownloadIcon, PrinterIcon, ShareIcon, SettingsIcon, ImageIcon, DollarSignIcon, ClipboardIcon, CheckIcon, InfoIcon, AlertCircleIcon, CopyIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const MerchantQRGenerator: React.FC = () => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [expiryHours, setExpiryHours] = useState('24');
  const [reference, setReference] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [showQR, setShowQR] = useState(false);
  const [qrURL, setQrURL] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState('medium');
  const [addLogo, setAddLogo] = useState(true);
  const [showColors, setShowColors] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#0066CC');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  // Mock QR code URL
  const mockQRURL = 'https://via.placeholder.com/300x300?text=QR+Code';
  // Templates for QR codes
  const templates = [{
    id: 'default',
    name: 'Default',
    preview: 'https://via.placeholder.com/80x80?text=Default'
  }, {
    id: 'branded',
    name: 'Branded',
    preview: 'https://via.placeholder.com/80x80?text=Branded'
  }, {
    id: 'minimal',
    name: 'Minimal',
    preview: 'https://via.placeholder.com/80x80?text=Minimal'
  }, {
    id: 'colorful',
    name: 'Colorful',
    preview: 'https://via.placeholder.com/80x80?text=Colorful'
  }];
  // Generate a random reference ID
  const generateReference = () => {
    const prefix = 'REF';
    const randomPart = Math.floor(100000 + Math.random() * 900000).toString();
    setReference(`${prefix}-${randomPart}`);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would call an API to generate the QR code
    // For this mock, we'll just set a placeholder image
    setQrURL(mockQRURL);
    setShowQR(true);
  };
  // Copy QR link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }, err => {
      console.error('Could not copy text: ', err);
    });
  };
  // Download QR code
  const downloadQR = () => {
    // In a real app, this would download the QR code image
    console.log('Downloading QR code');
  };
  // Print QR code
  const printQR = () => {
    // In a real app, this would open the print dialog
    console.log('Printing QR code');
  };
  // Share QR code
  const shareQR = () => {
    // In a real app, this would open a share dialog
    console.log('Sharing QR code');
  };
  return <div className="space-y-6" data-id="element-4781">
      <div data-id="element-4782">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4783">
          QR Code Generator
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4784">
          Create customized QR codes for payments
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4785">
        {/* QR Generator Form */}
        <div className="lg:col-span-2" data-id="element-4786">
          <Card data-id="element-4787">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-4788">
              Create New QR Code
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-4789">
              <div data-id="element-4790">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4791">
                  Amount (Optional)
                </label>
                <div className="relative rounded-md shadow-sm" data-id="element-4792">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4793">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-4794" />
                  </div>
                  <input type="number" id="amount" value={amount} onChange={e => setAmount(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" placeholder="Leave empty for customer to enter amount" step="0.01" min="0" data-id="element-4795" />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4796">
                  If left empty, the customer will be prompted to enter an
                  amount.
                </p>
              </div>
              <div data-id="element-4797">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4798">
                  Description
                </label>
                <input type="text" id="description" value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" placeholder="Payment for goods/services" data-id="element-4799" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4800">
                <div data-id="element-4801">
                  <label htmlFor="reference" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4802">
                    Reference ID
                  </label>
                  <div className="flex" data-id="element-4803">
                    <input type="text" id="reference" value={reference} onChange={e => setReference(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md shadow-sm text-sm bg-white dark:bg-gray-800" placeholder="Reference ID" data-id="element-4804" />
                    <button type="button" onClick={generateReference} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r-md text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600" data-id="element-4805">
                      Generate
                    </button>
                  </div>
                </div>
                <div data-id="element-4806">
                  <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4807">
                    Expiry (Hours)
                  </label>
                  <select id="expiry" value={expiryHours} onChange={e => setExpiryHours(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-4808">
                    <option value="1" data-id="element-4809">1 hour</option>
                    <option value="2" data-id="element-4810">2 hours</option>
                    <option value="6" data-id="element-4811">6 hours</option>
                    <option value="12" data-id="element-4812">12 hours</option>
                    <option value="24" data-id="element-4813">24 hours</option>
                    <option value="48" data-id="element-4814">48 hours</option>
                    <option value="72" data-id="element-4815">72 hours</option>
                    <option value="168" data-id="element-4816">1 week</option>
                    <option value="720" data-id="element-4817">30 days</option>
                  </select>
                </div>
              </div>
              <div data-id="element-4818">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4819">
                  QR Code Template
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-4820">
                  {templates.map(template => <div key={template.id} className={`cursor-pointer rounded-lg p-2 ${selectedTemplate === template.id ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'}`} onClick={() => setSelectedTemplate(template.id)} data-id="element-4821">
                      <div className="flex flex-col items-center" data-id="element-4822">
                        <img src={template.preview} alt={template.name} className="w-16 h-16 mb-2 rounded" data-id="element-4823" />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-4824">
                          {template.name}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-4825">
                <button type="button" onClick={() => setShowColors(!showColors)} className="flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-3" data-id="element-4826">
                  <SettingsIcon className="h-4 w-4 mr-2" data-id="element-4827" />
                  Advanced Options
                  {showColors ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-4828" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-4829" />}
                </button>
                {showColors && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3" data-id="element-4830">
                    <div data-id="element-4831">
                      <label htmlFor="qrSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4832">
                        QR Size
                      </label>
                      <select id="qrSize" value={qrSize} onChange={e => setQrSize(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-4833">
                        <option value="small" data-id="element-4834">Small</option>
                        <option value="medium" data-id="element-4835">Medium</option>
                        <option value="large" data-id="element-4836">Large</option>
                      </select>
                    </div>
                    <div data-id="element-4837">
                      <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4838">
                        Primary Color
                      </label>
                      <div className="flex" data-id="element-4839">
                        <input type="color" id="primaryColor" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="h-10 w-10 border border-gray-300 dark:border-gray-600 rounded-l-md" data-id="element-4840" />
                        <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-4841" />
                      </div>
                    </div>
                    <div data-id="element-4842">
                      <label htmlFor="backgroundColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4843">
                        Background Color
                      </label>
                      <div className="flex" data-id="element-4844">
                        <input type="color" id="backgroundColor" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} className="h-10 w-10 border border-gray-300 dark:border-gray-600 rounded-l-md" data-id="element-4845" />
                        <input type="text" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-4846" />
                      </div>
                    </div>
                    <div className="md:col-span-3" data-id="element-4847">
                      <div className="flex items-center" data-id="element-4848">
                        <input type="checkbox" id="addLogo" checked={addLogo} onChange={e => setAddLogo(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-4849" />
                        <label htmlFor="addLogo" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4850">
                          Add merchant logo to QR code
                        </label>
                      </div>
                    </div>
                  </div>}
              </div>
              <div className="flex justify-end" data-id="element-4851">
                <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-4852">
                  Generate QR Code
                </button>
              </div>
            </form>
          </Card>
        </div>
        {/* QR Code Preview */}
        <div data-id="element-4853">
          <Card data-id="element-4854">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-4855">
              QR Code Preview
            </h3>
            {showQR ? <div className="space-y-4" data-id="element-4856">
                <div className="flex justify-center" data-id="element-4857">
                  <div className="p-4 bg-white rounded-lg shadow-sm" data-id="element-4858">
                    <img src={qrURL} alt="Generated QR Code" className="w-full max-w-[250px] mx-auto" data-id="element-4859" />
                  </div>
                </div>
                <div className="text-center" data-id="element-4860">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4861">
                    {description || 'Payment QR Code'}
                  </p>
                  {amount && <p className="text-lg font-bold text-gray-900 dark:text-white mt-1" data-id="element-4862">
                      PKR {parseFloat(amount).toFixed(2)}
                    </p>}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4863">
                    Expires in {expiryHours} hours
                  </p>
                  <div className="mt-2 flex items-center justify-center" data-id="element-4864">
                    <div className="relative flex" data-id="element-4865">
                      <input type="text" value={qrURL} readOnly className="pr-10 pl-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 w-full" data-id="element-4866" />
                      <button onClick={copyToClipboard} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" data-id="element-4867">
                        {copied ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-4868" /> : <CopyIcon className="h-4 w-4" data-id="element-4869" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-4870">
                  <button onClick={downloadQR} className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" title="Download" data-id="element-4871">
                    <DownloadIcon className="h-5 w-5" data-id="element-4872" />
                  </button>
                  <button onClick={printQR} className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" title="Print" data-id="element-4873">
                    <PrinterIcon className="h-5 w-5" data-id="element-4874" />
                  </button>
                  <button onClick={shareQR} className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" title="Share" data-id="element-4875">
                    <ShareIcon className="h-5 w-5" data-id="element-4876" />
                  </button>
                </div>
              </div> : <div className="flex flex-col items-center justify-center py-8" data-id="element-4877">
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4" data-id="element-4878">
                  <QrCodeIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" data-id="element-4879" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center" data-id="element-4880">
                  Fill out the form and click "Generate QR Code" to create your
                  custom payment QR code.
                </p>
              </div>}
          </Card>
          <div className="mt-4" data-id="element-4881">
            <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-100 dark:border-yellow-800" data-id="element-4882">
              <div className="flex" data-id="element-4883">
                <div className="flex-shrink-0" data-id="element-4884">
                  <InfoIcon className="h-5 w-5 text-yellow-400" data-id="element-4885" />
                </div>
                <div className="ml-3" data-id="element-4886">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300" data-id="element-4887">
                    QR Code Tips
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-200 space-y-1" data-id="element-4888">
                    <p data-id="element-4889">
                      • QR codes with logos may be less scannable in poor
                      lighting
                    </p>
                    <p data-id="element-4890">• Use high contrast colors for better scan rates</p>
                    <p data-id="element-4891">• Print QR codes at least 2x2 cm for physical use</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      {/* QR Code History */}
      <Card data-id="element-4892">
        <div className="flex items-center justify-between mb-4" data-id="element-4893">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4894">
            Recently Generated QR Codes
          </h3>
          <a href="#" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300" data-id="element-4895">
            View All
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-4896">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4897">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-4898">
              <tr data-id="element-4899">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4900">
                  QR Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4901">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4902">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4903">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4904">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4905">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4906">
              <tr data-id="element-4907">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4908">
                  <img src="https://via.placeholder.com/40x40?text=QR" alt="QR Code" className="h-10 w-10 rounded" data-id="element-4909" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4910">
                  Payment for Electronics
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4911">
                  PKR 1,250.00
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4912">
                  Today, 10:30 AM
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4913">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-4914">
                    Active
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4915">
                  <div className="flex space-x-2" data-id="element-4916">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-4917">
                      <DownloadIcon className="h-4 w-4" data-id="element-4918" />
                    </button>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-4919">
                      <ShareIcon className="h-4 w-4" data-id="element-4920" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr data-id="element-4921">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4922">
                  <img src="https://via.placeholder.com/40x40?text=QR" alt="QR Code" className="h-10 w-10 rounded" data-id="element-4923" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4924">
                  Monthly Subscription
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4925">
                  PKR 750.00
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4926">
                  Yesterday, 2:15 PM
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4927">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-4928">
                    Active
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4929">
                  <div className="flex space-x-2" data-id="element-4930">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-4931">
                      <DownloadIcon className="h-4 w-4" data-id="element-4932" />
                    </button>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-4933">
                      <ShareIcon className="h-4 w-4" data-id="element-4934" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr data-id="element-4935">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4936">
                  <img src="https://via.placeholder.com/40x40?text=QR" alt="QR Code" className="h-10 w-10 rounded" data-id="element-4937" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4938">
                  Custom Order #123
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4939">
                  PKR 2,450.00
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4940">
                  Oct 25, 2023
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-4941">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-4942">
                    Expired
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4943">
                  <div className="flex space-x-2" data-id="element-4944">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-4945">
                      <DownloadIcon className="h-4 w-4" data-id="element-4946" />
                    </button>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-4947">
                      <ShareIcon className="h-4 w-4" data-id="element-4948" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};
export default MerchantQRGenerator;